import { AppMode, JaneDM } from '@iheartjane/dm-sdk';
import { getFlagContext, useFlagClient } from '@iheartjane/feature-flags';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { v4 as uuid } from 'uuid';

import { SDKProvider } from '@jane/shared-ecomm/providers';
import { statePostalCode } from '@jane/shared/util';

import type { RootLoaderType } from '../../data-access';
import { useKioskUser } from '../../data-access';
import { useTypedRouteLoaderData } from '../../hooks';
import { register } from '../../util';
import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';

interface SessionContext {
  flagClient: ReturnType<typeof useFlagClient>;
  jdid: string;
  resetSession: () => void;
  sessionId: string;
}

const SessionContext = createContext<SessionContext | null>(null);

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kioskUser } = useKioskUser();

  const flagClient = useFlagClient();
  const [queryParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string>(
    queryParams.get('sessionId') || uuid()
  );
  const [jdid, setJdid] = useState<string>(
    queryParams.get('jdid') || JaneDM.generateJaneDeviceId()
  );

  useEffect(() => {
    register({ 'Session ID': sessionId });
  }, [sessionId]);

  useEffect(() => {
    async function identifyContext() {
      await flagClient?.identify(
        getFlagContext({
          extraAttributes: {
            appMode: AppMode.Kiosk,
            state: statePostalCode(store?.state),
            storeId: store?.id,
            organizationId: store?.organization_id ?? undefined,
          },
          janeDeviceId: jdid,
          user: { ...kioskUser },
        })
      );
    }
    identifyContext();
  }, [flagClient, store, jdid, kioskUser]);

  const resetSession = useCallback(() => {
    setSessionId(uuid());
    setJdid(JaneDM.generateJaneDeviceId());
  }, []);

  const contextValue = useMemo(
    () => ({ flagClient, resetSession, sessionId, jdid }),
    [flagClient, jdid, resetSession, sessionId]
  );

  return (
    <SessionContext.Provider value={contextValue}>
      <SDKProvider kioskUser={kioskUser} jdid={jdid}>
        {children}
      </SDKProvider>
    </SessionContext.Provider>
  );
};

export const useSessionContext = () =>
  useEnsureContextWithinProvider(SessionContext, 'Session');
